import React from 'react';
import { Link } from 'react-router-dom';
import CaseStudyGate from '../../components/CaseStudyGate';
import Footer from '../../components/Footer';
import OtherProjects from '../../components/OtherProjects';
import useNoIndex from '../../hooks/useNoIndex';

// WhoSay brand blue, from the Editorial design system's color guide
// (#0086C5, "the WhoSay logo and brand blue"). It's too light for small text
// on the page background, so text uses a darker shade that clears WCAG AA.
const ACCENT = '#0086C5';
const ACCENT_TEXT = '#006FA3';

// Same lighter "overview" structure as the Flok case study: context, then
// what I designed, section by section, with no Outcome/metrics section.

const SectionHeading = ({ eyebrow, title, intro }) => (
  <div className="mb-12">
    <p className="text-sm font-bold uppercase tracking-widest mb-3" style={{ color: ACCENT_TEXT }}>
      {eyebrow}
    </p>
    <h2 className="text-3xl sm:text-4xl font-bold text-text-primary mb-4">{title}</h2>
    {intro && <p className="text-tertiary text-lg max-w-2xl">{intro}</p>}
  </div>
);

const SubHeading = ({ title, children }) => (
  <div className="mb-10">
    <h3 className="text-2xl font-bold text-text-primary mb-3">{title}</h3>
    <p className="text-tertiary text-lg max-w-2xl">{children}</p>
  </div>
);

// Small uppercase label above a group of images (e.g. "iOS", "Design system").
const GroupLabel = ({ children }) => (
  <p className="text-xs font-bold uppercase tracking-widest text-tertiary mb-4">{children}</p>
);

// Caption in the case studies' shared style: a bold label, then the description.
const Caption = ({ label, children, className = '' }) => (
  <p className={`text-sm text-tertiary ${className}`}>
    {label && (
      <span className="font-bold mr-1.5" style={{ color: ACCENT_TEXT }}>
        {label}
      </span>
    )}
    {children}
  </p>
);

// A single flat phone screenshot (status bar included, no bezel).
const PhoneScreen = ({ src, alt }) => (
  <img src={src} alt={alt} className="w-full max-w-[240px] h-auto mx-auto shadow-xl border border-gray-light" />
);

const ANDROID_SCREENS = [
  {
    file: 'my-posts',
    alt: 'Android My Posts screen: 15,804,290 total fans, an On Fire past-7-days summary, and top posts by engagement and replies',
    label: 'My Posts',
    caption: 'Total fans and top-performing content at a glance',
  },
  {
    file: 'my-news',
    alt: 'Android My News screen: News Staring You, a daily feed of celebrity-relevant stories, each with a reshare button',
    label: 'My News',
    caption: 'Celebrity-relevant stories, ready to reshare',
  },
];

// A phone that already has its own device bezel, on a transparent background.
const DevicePhone = ({ src, alt }) => (
  <img src={src} alt={alt} className="w-full max-w-[240px] h-auto mx-auto" />
);

// The top of a full-page capture, cropped to a fixed height from the top.
const CroppedShot = ({ src, alt, label, height = 380 }) => (
  <figure>
    <div className="overflow-hidden bg-white shadow-xl border border-gray-light" style={{ height }}>
      <img src={src} alt={alt} className="w-full h-auto block" />
    </div>
    <figcaption className="mt-3">
      <Caption label={label} />
    </figcaption>
  </figure>
);

// A full-length page capture (much taller than the viewport) shown in a
// fixed-height frame that scrolls, so the whole page is viewable without
// taking over the case study. Focusable so it can be scrolled by keyboard.
const ScrollShot = ({ src, alt, label, height = 560, maxWidth = 320 }) => (
  <figure className="mx-auto w-full" style={{ maxWidth }}>
    <div
      className="overflow-y-auto bg-white shadow-xl border border-gray-light focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0086C5] focus-visible:ring-offset-2"
      style={{ height }}
      tabIndex={0}
      role="region"
      aria-label={`${label}, scrollable full-page screenshot`}
    >
      <img src={src} alt={alt} className="w-full h-auto block" />
    </div>
    <figcaption className="mt-3">
      <Caption label={label}>Scroll to see the full page</Caption>
    </figcaption>
  </figure>
);

// The iOS row: three separate screens. The first is a phone cut out of the
// original device mockup (it has its own bezel); the other two are flat
// onboarding screens.
const IOS_SCREENS = [
  {
    file: 'talent-welcome',
    bezel: true,
    alt: 'Welcome to WhoSay onboarding screen: "The exclusive publishing tool for the world\'s top digital influencers," previewing 209,504 total fans and top posts',
    label: 'Welcome & total fans',
    caption: 'The onboarding screen introducing the total-fan count',
  },
  {
    file: 'onboarding-email-fans',
    alt: 'Onboarding screen: Email your fans, explaining that WhoSay collects fan email addresses so the celebrity can email them from the app',
    label: 'Email your fans',
    caption: "WhoSay collects fans' emails, so talent can email them from the app",
  },
  {
    file: 'onboarding-reach-500m-fans',
    alt: 'Onboarding screen: Reach 500M+ fans, showing a post publishing to People, Huffington Post, Flipboard, and Tango',
    label: 'Reach 500M+ fans',
    caption: 'One post, published to People, Huffington Post, Flipboard, and Tango',
  },
];

// Flow chart palette shared with the Herba and Flok case studies: light gray
// panels with solid, borderless step boxes, echoing the Culture Amp journey row.
const WIRE = {
  card: '#ECEEF1',
  node: '#56697C',
  ink: '#2B3A4A',
  onNode: '#FFFFFF',
  onNodeMuted: 'rgba(255, 255, 255, 0.8)',
  muted: '#8A94A0',
  accent: '#4F7FA0',
  highlight: '#3F6F91',
};

// A step in a flow chart. `end` marks the final step with a darker box, and
// `highlight` tints a step blue to call out what the app did for the user.
const FlowNode = ({ label, sub, end, highlight }) => (
  <div
    className="rounded-lg px-4 py-3 text-center text-sm font-medium"
    style={{
      backgroundColor: end ? WIRE.ink : highlight ? WIRE.highlight : WIRE.node,
      color: WIRE.onNode,
    }}
  >
    {label}
    {sub && (
      <span className="block text-xs font-normal mt-1" style={{ color: WIRE.onNodeMuted }}>
        {sub}
      </span>
    )}
  </div>
);

const Arrow = () => (
  <div className="text-center leading-none py-2" style={{ color: WIRE.muted }} aria-hidden="true">
    ↓
  </div>
);

// Talent app posting flow: one post, composed once, published everywhere.
const POSTING_FLOW = [
  { label: 'Compose a post' },
  { label: 'Select networks', sub: 'Facebook, Twitter, Instagram, Google+, Tumblr', highlight: true },
  { label: 'Expand "Also Publish To"', sub: 'People, Huffington Post, Flipboard, Tango' },
  { label: 'Adapts per platform', sub: "Image auto-removed for Twitter, which didn't support images yet", highlight: true },
  { label: 'Publish everywhere at once' },
  { label: 'Analyze post reactions', end: true },
];

const PostingFlow = () => (
  <div
    className="rounded-lg p-6 sm:p-8 max-w-md mx-auto"
    style={{ backgroundColor: WIRE.card }}
    role="img"
    aria-label="Talent app posting flow: compose a post; select networks (Facebook, Twitter, Instagram, Google+, Tumblr); expand Also Publish To (People, Huffington Post, Flipboard, Tango); the post adapts per platform, with the image auto-removed for Twitter, which didn't support images yet; publish everywhere at once; then analyze post reactions."
  >
    <FlowTitle>Compose to publish</FlowTitle>
    {POSTING_FLOW.map((step, i) => (
      <React.Fragment key={step.label}>
        {i > 0 && <Arrow />}
        <FlowNode {...step} />
      </React.Fragment>
    ))}
  </div>
);

// Match Report flow: from a brand's request to an exported shortlist.
const MATCH_FLOW = [
  { label: 'Brand requests a campaign' },
  { label: 'Sales enters criteria', sub: 'Keywords, plus fan age and gender preferences' },
  { label: 'Match Report ranks celebrities', sub: 'Scored by brand affinity, audience size, age, and gender fit' },
  { label: 'Sales edits the results', sub: 'Adjust criteria, add or remove names' },
  { label: 'Export and send to brand', end: true },
];

const FlowTitle = ({ children }) => (
  <p className="text-[10px] font-bold uppercase tracking-widest text-center mb-3" style={{ color: WIRE.accent }}>
    {children}
  </p>
);

const OUTREACH = [
  { label: 'Sales sends a direct message', sub: 'In the Talent app, with campaign details and payment' },
  { label: 'Celebrity reviews the offer' },
];

const OutcomePair = () => (
  <div className="grid grid-cols-2 gap-3">
    <FlowNode label="Accept" sub="Schedule a meeting" end />
    <FlowNode label="Decline" />
  </div>
);

// Pieces of the right-angle connector from "Export and send to brand" (bottom
// of the left column) up to "Sales sends a direct message" (top of the right
// column). Each piece fills its grid cell, so the line stays attached to the
// boxes' centers whatever height the rows end up.
const LINE = `1px solid ${WIRE.muted}`;

const ConnectorTop = () => (
  <div className="relative flex-1" aria-hidden="true">
    <div className="absolute left-1/2 top-1/2 bottom-0" style={{ borderLeft: LINE }} />
    <div className="absolute left-1/2 right-1 top-1/2" style={{ borderTop: LINE }} />
    <svg className="absolute right-0 top-1/2 -translate-y-1/2" width="6" height="8" viewBox="0 0 6 8">
      <path d="M0 0 L6 4 L0 8 z" fill={WIRE.muted} />
    </svg>
  </div>
);

const ConnectorMiddle = ({ style }) => (
  <div className="relative self-stretch" style={style} aria-hidden="true">
    <div className="absolute left-1/2 top-0 bottom-0" style={{ borderLeft: LINE }} />
  </div>
);

const ConnectorBottom = ({ style }) => (
  <div className="relative self-stretch" style={style} aria-hidden="true">
    <div className="absolute left-1/2 top-0 bottom-1/2" style={{ borderLeft: LINE }} />
    <div className="absolute left-0 right-1/2 top-1/2" style={{ borderTop: LINE }} />
  </div>
);

// Two phases side by side: Match Report builds the shortlist, then Outreach
// takes it to the talent. On wide screens both flows sit on one shared grid
// (so their rows line up, top-aligned) with a connector between them; on
// narrow screens the phases simply stack.
const MatchReportFlow = () => {
  // Left column: node, arrow, node, ... (9 rows). Right column: 5 rows.
  const left = MATCH_FLOW.flatMap((step, i) => (i > 0 ? [null, step] : [step]));
  const right = [OUTREACH[0], null, OUTREACH[1], null, 'pair'];
  const cell = (item, key, col, row) => (
    <div key={key} style={{ gridColumn: col, gridRow: row }}>
      {item === null ? <Arrow /> : item === 'pair' ? <OutcomePair /> : <FlowNode {...item} />}
    </div>
  );

  return (
    <div
      className="rounded-lg p-6 sm:p-8 max-w-4xl mx-auto"
      style={{ backgroundColor: WIRE.card }}
      role="img"
      aria-label="Match Report flow: a brand requests a campaign; sales enters criteria (keywords, plus fan age and gender preferences); Match Report ranks celebrities, scored by brand affinity, audience size, age, and gender fit; sales edits the results; then exports and sends them to the brand. Outreach then follows: sales sends a direct message in the Talent app with campaign details and payment; the celebrity reviews the offer; and either accepts, scheduling a meeting, or declines."
    >
      {/* Wide screens: shared grid with the connector in the middle column */}
      <div className="hidden md:grid grid-cols-[1fr_48px_1fr] items-center">
        <div style={{ gridColumn: 1, gridRow: 1 }}>
          <FlowTitle>Report</FlowTitle>
        </div>
        <div style={{ gridColumn: 3, gridRow: 1 }}>
          <FlowTitle>Outreach</FlowTitle>
        </div>
        {left.map((item, i) => cell(item, `l${i}`, 1, i + 2))}
        {right.map((item, i) => cell(item, `r${i}`, 3, i + 2))}
        <div className="self-stretch flex" style={{ gridColumn: 2, gridRow: 2 }}>
          <ConnectorTop />
        </div>
        <ConnectorMiddle style={{ gridColumn: 2, gridRow: `3 / ${left.length + 1}` }} />
        <ConnectorBottom style={{ gridColumn: 2, gridRow: left.length + 1 }} />
      </div>

      {/* Narrow screens: Match Report, then Outreach, stacked */}
      <div className="md:hidden">
        <FlowTitle>Report</FlowTitle>
        {left.map((item, i) => (
          <React.Fragment key={i}>{item === null ? <Arrow /> : <FlowNode {...item} />}</React.Fragment>
        ))}
        <Arrow />
        <div className="mt-2">
          <FlowTitle>Outreach</FlowTitle>
        </div>
        {right.map((item, i) => (
          <React.Fragment key={i}>
            {item === null ? <Arrow /> : item === 'pair' ? <OutcomePair /> : <FlowNode {...item} />}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

const WhoSay = () => {
  useNoIndex();
  const IMG = '/images/case-studies/whosay';

  return (
    <CaseStudyGate projectName="WhoSay">
      <div className="min-h-screen bg-primary">
        <div className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="pt-8">
            <Link
              to="/"
              className="inline-flex items-center text-tertiary hover:text-[#006FA3] transition-colors duration-300"
            >
              ← Back to Home
            </Link>
          </div>

          {/* Header */}
          <header className="pt-10 pb-16 sm:pt-16 sm:pb-20">
            <img src="/images/whosay_logo.png" alt="WhoSay" className="h-8 sm:h-10 object-contain mb-8" />
            <h1 className="text-4xl sm:text-5xl font-bold text-text-primary leading-tight mb-6 max-w-3xl">
              Designing the publishing tool celebrities used to reach every platform at once, as one
              of the original pioneers of influencer marketing.
            </h1>
            <p className="text-tertiary text-sm mb-6"><span className="font-bold">Senior Product Designer</span> · June 2014 - March 2016</p>
            <div className="w-16 h-1 mb-8" style={{ backgroundColor: ACCENT }} />
            <div className="grid grid-cols-[2fr_1fr] gap-6 text-sm max-w-3xl">
              <div>
                <p className="text-tertiary mb-1">Team</p>
                <p className="text-text-primary font-medium">
                  Sole designer, working directly with celebrities and their social media managers, and collaborating closely with the engineering team. Designed the Talent app (iOS and Android), the Editorial fan site, and, after the pivot, Match Report, an affinity dashboard for the sales team.
                </p>
              </div>
              <div>
                <p className="text-tertiary mb-1">Timeline</p>
                <p className="text-text-primary font-medium">June 2014 - March 2016</p>
              </div>
              <div>
                <p className="text-tertiary mb-1">Goal</p>
                <p className="text-text-primary font-medium">
                  Give celebrities one place to compose a post and publish it everywhere at once.
                </p>
              </div>
              <div>
                <p className="text-tertiary mb-1">Deliverable</p>
                <p className="text-text-primary font-medium">
                  Shipped product, iOS and Android, plus a companion web platform
                </p>
              </div>
            </div>
          </header>

          {/* Context */}
          <section className="py-16 sm:py-24 border-t border-gray-light">
            <SectionHeading eyebrow="Context" title="One post, every platform, all at once" />
            <p className="text-tertiary text-lg max-w-3xl">
              Celebrities didn't want to write five versions of the same post for five apps. WhoSay
              let them compose once and publish to Facebook, Twitter, Instagram, and approved news
              outlets like People, Huffington Post, and Tango, from a single screen, at a time when
              Instagram itself was still new. Fans got one place to follow a celebrity across every
              platform.
            </p>
          </section>

          {/* Talent app */}
          <section className="py-16 sm:py-24 border-t border-gray-light">
            <SectionHeading
              eyebrow="What I designed"
              title="Talent app"
              intro="The tool celebrities and their teams used every day: compose a post, publish it everywhere at once, and see how it performed without digging through five separate apps."
            />

            <div className="mb-16">
              <PostingFlow />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-10 items-end">
              {IOS_SCREENS.map(({ file, bezel, alt, label, caption }) => (
                <figure key={file}>
                  {bezel ? (
                    <DevicePhone src={`${IMG}/${file}.webp`} alt={alt} />
                  ) : (
                    <PhoneScreen src={`${IMG}/${file}.webp`} alt={alt} />
                  )}
                  <figcaption className="mt-5 max-w-[260px] mx-auto">
                    <Caption label={label} className="sm:text-center">
                      {caption}
                    </Caption>
                  </figcaption>
                </figure>
              ))}
            </div>

            <div className="mt-24">
              <SubHeading title="Feel-good analytics, on purpose">
                Performance data was framed as a reward, not a report: total fans and top posts on
                one screen, celebrity-relevant news ready to reshare on another. Built to feel like a
                win worth checking, not a dashboard to dread.
              </SubHeading>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-10 max-w-2xl mx-auto">
                {ANDROID_SCREENS.map(({ file, alt, label, caption }) => (
                  <figure key={file}>
                    <DevicePhone src={`${IMG}/${file}.webp`} alt={alt} />
                    <figcaption className="mt-5 max-w-[260px] mx-auto">
                      <Caption label={label} className="sm:text-center">
                        {caption}
                      </Caption>
                    </figcaption>
                  </figure>
                ))}
              </div>
            </div>

            <div className="mt-24">
              <SubHeading title="Knowing who's paying attention">
                The reply feed showed each commenter's own follower count, so a celebrity could see
                at a glance when another high-profile person, not just a fan, had replied.
              </SubHeading>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-10 max-w-2xl mx-auto">
                <figure>
                  <DevicePhone
                    src={`${IMG}/fan-activity.webp`}
                    alt="Android Fan Activity screen with a live replies feed, like and reply counts, and a top fan reply from Sarah Neto showing her 900K followers"
                  />
                  <figcaption className="mt-5 max-w-[260px] mx-auto">
                    <Caption label="Fan Activity" className="sm:text-center">
                      Top commenters across every post, other celebrities and high-follower accounts
                      first, ahead of regular fan replies
                    </Caption>
                  </figcaption>
                </figure>
                <figure>
                  <PhoneScreen
                    src={`${IMG}/congrats-popup.webp`}
                    alt={'Congrats pop-up: "You probably just made this person\'s day... or year!" after a celebrity replies to a fan'}
                  />
                  <figcaption className="mt-5 max-w-[260px] mx-auto">
                    <Caption label="Congrats pop-up" className="sm:text-center">
                      Shown once per session, the first time a celebrity likes or replies to a
                      comment, then never again that session
                    </Caption>
                  </figcaption>
                </figure>
              </div>
            </div>
          </section>

          {/* Editorial (fan site) */}
          <section className="py-16 sm:py-24 border-t border-gray-light">
            <SectionHeading
              eyebrow="What I designed"
              title="Editorial (fan site)"
              intro="A test: could celebrity content posted through WhoSay's own platform support an advertising business, the way traditional celebrity press did? The Editorial site and app let fans browse a celebrity's posts and news in one place. Alongside it, I built a full design system (color, type, buttons, and components) so a small team could build consistently."
            />

            <img
              src={`${IMG}/editorial-app.webp`}
              alt="Three WhoSay fan app screens: Add Favorites, searching for and following Eva Longoria; See & Share, her celebrity profile; and Get News, a trending story with celebs active now"
              className="w-full h-auto"
            />

            <div className="mt-16">
              <GroupLabel>Website navigation</GroupLabel>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <CroppedShot
                  src={`${IMG}/web-nav-default.webp`}
                  label="Default"
                  alt="WhoSay website Style section with the default top navigation, a featured article, top stories, and a grid of posts"
                />
                <CroppedShot
                  src={`${IMG}/web-nav-search.webp`}
                  label="Search open"
                  alt="WhoSay website with the search field open in the top navigation, dimming the page behind it"
                />
                <CroppedShot
                  src={`${IMG}/web-nav-search-hover.webp`}
                  label="Search results"
                  alt="WhoSay website search showing live results grouped into celebrities, articles, and tags"
                />
              </div>
            </div>

            <div className="mt-16">
              <GroupLabel>Design system</GroupLabel>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <ScrollShot
                  src={`${IMG}/design-color-guide.webp`}
                  label="Color guide"
                  height={480}
                  alt="Color guide with WhoSay brand blue #0086C5, hover blue, error red, accent green, and grays, each with its usage"
                />
                <ScrollShot
                  src={`${IMG}/design-buttons.webp`}
                  label="Buttons & drop-downs"
                  height={480}
                  alt="Button and drop-down specs with spacing and sizes for web and mobile, hover states, and the sign-in drop-down"
                />
                <ScrollShot
                  src={`${IMG}/design-copy-styles.webp`}
                  label="Copy styles"
                  height={480}
                  alt="Copy style specs for section headers, article headlines, bylines, and social icon colors, with font, size, color, and line height"
                />
              </div>
            </div>
          </section>

          {/* The pivot: Match Report */}
          <section className="py-16 sm:py-24 border-t border-gray-light">
            <SectionHeading
              eyebrow="What I designed"
              title="The pivot: Match Report"
              intro="The company shifted from advertising to brokering social media deals between celebrities and brands. I designed Match Report, an affinity dashboard for the sales team: enter a brand's keywords and fan demographics (age, gender), and it ranks and scores the celebrities whose audience fits, ready to export straight to the brand. With thousands of celebrities already on the platform, sales could pitch matching talent to brands proactively instead of waiting for inbound requests."
            />
            <MatchReportFlow />
            <figure className="mt-16">
              <img
                src={`${IMG}/match-report-detail.webp`}
                alt="Match Report for a Chevy Imoji Academy campaign on mobile and desktop, ranking celebrities like Kerry Washington, Chris Rock, and Gwen Stefani by brand affinity and WhoSay score, with social reach, age, and gender breakdowns"
                className="w-full h-auto"
              />
              <figcaption className="mt-5 max-w-3xl">
                <Caption label="The full dashboard">
                  Shown in both mobile and desktop, this Chevy Imoji Academy example ranks Kerry
                  Washington, Chris Rock, and Gwen Stefani by affinity and reach
                </Caption>
              </figcaption>
            </figure>
          </section>

          {/* Where WhoSay stands now */}
          <section className="py-16 sm:py-24 border-t border-gray-light">
            <h2 className="text-2xl sm:text-3xl font-bold text-text-primary mb-6 max-w-2xl">
              Where WhoSay stands now
            </h2>
            <p className="text-tertiary text-lg max-w-3xl">
              WhoSay was acquired by Viacom, after becoming the first major platform connecting
              influencers with brands for social media campaigns backed by measured results.
            </p>
          </section>

          <OtherProjects current="whosay" />
        </div>
      </div>
      <Footer />
    </CaseStudyGate>
  );
};

export default WhoSay;
